import { useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, ShieldAlert, CheckCircle2 } from "lucide-react";
import LegalPage from "../components/LegalPage";
import { SITE } from "../site";
import { useAuth } from "../auth/AuthContext";
import { useDeleteAccount } from "../api/hooks";
import { apiError } from "../api/client";

const SECTIONS = [
    {
        heading: "Requesting deletion",
        body: [
            `To request deletion of your SahiKhoj app account and associated personal data, email us at ${SITE.supportEmail} with the subject line “Account Deletion Request”.`,
            "Please send the email from the email address registered with your SahiKhoj account, and include your registered mobile number if you used one to sign up. This helps us verify that the request is genuine.",
        ],
    },
    {
        heading: "What happens next",
        body: [
            "Once we receive your request, we will verify your identity and confirm the request by replying to your email. After verification, we will delete your account and associated personal data, and let you know when it is done.",
        ],
    },
    {
        heading: "What gets deleted",
        body: [
            "Deletion covers your account profile (such as name, email address and mobile number, where provided) and personal data associated with your account. Once deleted, your account cannot be recovered.",
        ],
    },
    {
        heading: "What may be retained",
        body: [
            "Certain records may be retained where required by applicable law or for legitimate purposes such as fraud prevention, security or resolving disputes. Any retained data is kept only for as long as necessary for those purposes.",
        ],
    },
    {
        heading: "Deleting the app without an account",
        body: [
            "If you use SahiKhoj without creating an account, simply uninstalling the app removes it from your device. No deletion request is needed.",
        ],
    },
];

// Logged-in users can delete their account instantly via the app backend's
// account-deletion endpoint; the email flow below remains for everyone else.
const DirectDeletePanel = () => {
    const { user, logout } = useAuth();
    const del = useDeleteAccount();
    const [password, setPassword] = useState("");
    const [confirmation, setConfirmation] = useState("");
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    if (!user) return null;
    if (done) {
        return (
            <section className="bg-cream px-6 pt-16 sm:px-8" data-testid="delete-done">
                <div className="mx-auto flex max-w-3xl items-start gap-4 rounded-3xl border border-leaf/30 bg-leaf/10 p-8">
                    <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-leaf" />
                    <div>
                        <h2 className="font-display text-xl font-semibold text-ink">Your account has been deleted</h2>
                        <p className="mt-2 text-sm leading-relaxed text-smoke">
                            Your SahiKhoj account and associated personal data have been removed.
                            Some records may be retained where required by law, as described below.
                        </p>
                        <Link to="/" className="mt-4 inline-flex rounded-full bg-ink px-6 py-3 text-sm font-medium text-cream transition-colors hover:bg-india">
                            Back to home
                        </Link>
                    </div>
                </div>
            </section>
        );
    }

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        try {
            await del.mutateAsync({ confirmation, password: password || undefined });
            setDone(true);
            logout();
        } catch (err) {
            setError(apiError(err));
        }
    };

    return (
        <section className="bg-cream px-6 pt-16 sm:px-8" data-testid="direct-delete-panel">
            <div className="mx-auto max-w-3xl rounded-3xl border border-red-200 bg-white p-8 shadow-sm">
                <div className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-2xl bg-red-50">
                        <ShieldAlert className="h-5 w-5 text-red-600" />
                    </span>
                    <h2 className="font-display text-xl font-semibold tracking-tight text-ink">Delete your account now</h2>
                </div>
                <p className="mt-3 text-sm leading-relaxed text-smoke">
                    You are signed in as <span className="font-medium text-ink">{user.email}</span>. This
                    permanently deletes your account and personal data. This cannot be undone.
                </p>
                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="direct-delete-form">
                    {user.authProvider !== "google" && (
                        <input
                            type="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            placeholder="Your account password"
                            aria-label="Account password"
                            data-testid="delete-password-input"
                            className="w-full rounded-xl border border-ink/10 bg-cream/50 px-4 py-3 text-sm outline-none focus:border-red-400"
                        />
                    )}
                    <input
                        type="text"
                        value={confirmation}
                        onChange={(e) => setConfirmation(e.target.value)}
                        placeholder='Type DELETE to confirm'
                        aria-label="Type DELETE to confirm"
                        data-testid="delete-confirmation-input"
                        className="w-full rounded-xl border border-ink/10 bg-cream/50 px-4 py-3 text-sm outline-none focus:border-red-400"
                    />
                    {error && <p data-testid="delete-error" className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>}
                    <button
                        type="submit"
                        disabled={del.isPending || confirmation !== "DELETE"}
                        data-testid="delete-confirm-button"
                        className="inline-flex items-center gap-2 rounded-full bg-red-600 px-7 py-3.5 text-sm font-medium text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                    >
                        <Trash2 className="h-4 w-4" />
                        {del.isPending ? "Deleting…" : "Permanently delete my account"}
                    </button>
                </form>
            </div>
        </section>
    );
};

const DeleteAccountPage = () => (
    <>
        <DirectDeletePanel />
        <LegalPage
            title="Delete Account / Data Request"
            eyebrow="SahiKhoj · Your data"
            updated="July 2026"
            description="How to request deletion of your SahiKhoj app account and the personal data associated with it."
            path="/delete-account"
            intro="You can delete your SahiKhoj account instantly while signed in above, or ask us to delete it and the personal data associated with it. Here is how."
            sections={SECTIONS}
            testId="delete-account-page"
        />
    </>
);

export default DeleteAccountPage;
